/**
 * Shared page-level state components.
 * Eliminates the identical loading / error JSX duplicated across
 * Dashboard.tsx, Anomaly.tsx, and any future pages.
 */

import { AlertTriangle, WifiOff } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonCard, SkeletonChart } from '@/components/ui/Skeleton';

// ─── Loading skeleton ────────────────────────────────────────────────────────

export function PageLoadingSkeleton() {
  return (
    <div className="space-y-3 pt-3 animate-fade-in">
      <SkeletonCard />
      <div className="grid grid-cols-3 gap-3">
        <SkeletonCard /><SkeletonCard /><SkeletonCard />
      </div>
      <SkeletonChart />
      <SkeletonChart />
    </div>
  );
}

// ─── Error state (no data available) ─────────────────────────────────────────

interface PageErrorProps {
  message?: string | null;
  onRetry: () => void;
}

export function PageErrorState({ message, onRetry }: PageErrorProps) {
  return (
    <div className="pt-3">
      <EmptyState
        icon={<WifiOff className="w-10 h-10" />}
        title="Couldn't reach the data sheet"
        description={
          message ?? 'Check your network or sheet sharing settings (Anyone with the link → Viewer).'
        }
        action={
          <button
            onClick={onRetry}
            className="px-4 h-10 rounded-full bg-ink-900 text-white font-semibold text-sm active:scale-95 transition"
          >
            Try again
          </button>
        }
      />
    </div>
  );
}

// ─── Stale data warning banner ────────────────────────────────────────────────

export function StaleDataBanner() {
  return (
    <div className="card card-pad bg-signal-warn/5 border-signal-warn/20">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-signal-warn shrink-0 mt-0.5" />
        <p className="text-xs text-ink-700 leading-snug">
          <span className="font-semibold">Couldn't refresh just now.</span>{' '}
          Showing cached data — we'll keep retrying automatically.
        </p>
      </div>
    </div>
  );
}

// ─── Page title block ─────────────────────────────────────────────────────────

interface PageHeaderProps {
  title: string;
  description?: string;
}

export function PageHeader({ title, description }: PageHeaderProps) {
  return (
    <div className="px-1">
      <h1 className="font-display text-xl font-semibold text-ink-900">{title}</h1>
      {description && (
        <p className="text-[11px] text-ink-500 mt-0.5">{description}</p>
      )}
    </div>
  );
}
